// useConfirmation — state machine for the service-confirmation flow.
//
// On mount: resolves the token, maps the server response to a state.
// Exposes `submit(action)` which POSTs the decision and maps the response
// back to a new state. Booking data is projected to a privacy-safe
// `BookingView` before being handed to any component.

import { useEffect, useRef, useState } from 'react';
import { fetchChatIdentity, resolveToken, submitConfirmation } from '../utils/api';
import type {
  BookingView,
  ConfirmAction,
  ConfirmState,
  DidNotAttendDetail,
  DidNotAttendReason,
  ResolveResponse,
} from '../types';

/** 12-char nanoid-alphabet token inside a /c/:token pathname. */
export const TOKEN_PATH_RE = /^\/c\/([A-Za-z0-9_-]{12})\/?$/;

/** HubSpot chat embed URL (same hub ID as the previous static <script> tag
 *  in index.html, moved to programmatic load so we can set
 *  `hsConversationsSettings` BEFORE the loader runs — otherwise identification
 *  is silently ignored and the widget falls back to anonymous). */
const HUBSPOT_CHAT_SCRIPT_SRC = '//js-ap1.hs-scripts.com/441615826.js';
const HUBSPOT_CHAT_SCRIPT_ID = 'hs-script-loader';

/** Module-level guard: ensure we only inject the chat <script> tag once per
 *  page load even if useConfirmation re-runs (token change, Strict Mode
 *  double-invoke, etc.). */
let chatWidgetLoaded = false;

/** Reset the load guard — test-only. */
export function __resetChatWidgetLoadedForTests(): void {
  chatWidgetLoaded = false;
}

/**
 * Load the HubSpot chat widget once per session. If `identificationEmail` and
 * `identificationToken` are both present, set them on `window.hsConversationsSettings`
 * BEFORE appending the loader script — the widget reads the global at script
 * evaluation time, so any later mutation is ignored. Absent fields → anonymous
 * fallback (matches previous behaviour).
 */
function loadChatWidget(
  identification?: { identificationEmail: string; identificationToken: string },
): void {
  if (chatWidgetLoaded) return;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (document.getElementById(HUBSPOT_CHAT_SCRIPT_ID)) {
    chatWidgetLoaded = true;
    return;
  }

  if (identification) {
    // Set hsConversationsSettings BEFORE the loader script runs — HubSpot
    // reads this global at script-evaluation time. Do NOT set
    // loadImmediately:false — that requires a manual widget.load() call
    // we don't make, and would prevent the widget from rendering at all.
    // Default behaviour (auto-load) is what we want, with our identification
    // settings picked up on first init.
    (window as unknown as { hsConversationsSettings?: Record<string, unknown> }).hsConversationsSettings = {
      identificationEmail: identification.identificationEmail,
      identificationToken: identification.identificationToken,
    };
  }

  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.id = HUBSPOT_CHAT_SCRIPT_ID;
  s.async = true;
  s.defer = true;
  s.src = HUBSPOT_CHAT_SCRIPT_SRC;
  document.body.appendChild(s);
  chatWidgetLoaded = true;
}

export function extractTokenFromPath(pathname: string): string | null {
  const m = TOKEN_PATH_RE.exec(pathname);
  return m ? m[1] : null;
}

/** Map a raw booking row from the API into the UI-safe projection. */
export function toBookingView(booking: ResolveResponse['bookings'][number]): BookingView {
  const provider = (booking.provider_name ?? '').trim();
  return {
    id: booking.id,
    service_type: booking.service_type,
    start_time: booking.start_time,
    end_time: booking.end_time,
    provider_name: provider || null,
    status: booking.status,
    service_confirmed: booking.service_confirmed,
  };
}

/** Map the full bookings array, filtering out anything already terminal —
 *  the server's SELECT already excludes these but we mirror the predicate
 *  client-side in case a stale payload sneaks through. */
export function toBookingViews(bookings: ResolveResponse['bookings']): BookingView[] {
  return bookings
    .map(toBookingView)
    .filter(
      (b) =>
        b.status !== 'cancelled' &&
        (b.service_confirmed === null || b.service_confirmed === 'pending'),
    );
}

export interface UseConfirmationResult {
  state: ConfirmState;
  bookings: BookingView[];
  /** Per-booking submission status while the bundle is in flight or after
   *  it completes. Keyed by booking id. */
  submittedActions: Record<string, ConfirmAction>;
  /** Submit one answer; the hook POSTs and updates per-booking status.
   *  Returns true on success, false on any failure (so callers iterating
   *  over a bundle can break on the first failure rather than firing
   *  redundant POSTs after the hook has already moved to a terminal
   *  state). When all bookings have a submission, state moves to
   *  'submitted'. For a `did_not_attend` answer the caller may pass the
   *  captured `reason` (+ optional per-reason `detail`); these ride the POST
   *  body. The `submittedActions` receipt map stays keyed by action only —
   *  the reason isn't needed to render the per-booking status. */
  submit: (
    bookingId: string,
    action: ConfirmAction,
    reason?: DidNotAttendReason | null,
    detail?: DidNotAttendDetail | null,
  ) => Promise<boolean>;
}

export function useConfirmation(token: string | null): UseConfirmationResult {
  const [state, setState] = useState<ConfirmState>(token ? 'loading' : 'not_found');
  const [bookings, setBookings] = useState<BookingView[]>([]);
  const [submittedActions, setSubmittedActions] = useState<Record<string, ConfirmAction>>({});
  // Mirror submittedActions in a ref so sequential submit() calls in a
  // multi-visit bundle see the freshest map. The setState closure captures
  // the value at render time, and React doesn't necessarily commit between
  // awaited calls in the same event handler — without this, looping
  // submit('b1'), submit('b2'), submit('b3') would each see the same
  // initial {} and only the last write would stick.
  const submittedActionsRef = useRef<Record<string, ConfirmAction>>({});

  useEffect(() => {
    if (!token) {
      setState('not_found');
      return;
    }

    let cancelled = false;

    (async () => {
      const result = await resolveToken(token);
      if (cancelled) return;

      // ---- Page-state transitions (unchanged) ----
      if (result.success) {
        const views = toBookingViews(result.bookings);
        setBookings(views);
        // Empty bookings = staff cleared everything since the SMS went out.
        setState(views.length === 0 ? 'all_caught_up' : 'ready');
      } else if (result.status === 404) {
        setState('not_found');
      } else if (result.status === 410) {
        // SUPERSEDED is the cascade-killed case ("you're all up to date");
        // bare 410 is naturally expired ("this link has expired").
        setState(result.code === 'SUPERSEDED' ? 'superseded' : 'expired');
      } else {
        setState('error');
      }

      // ---- Chat identification (independent concern) ----
      // Prefer resolve-side identification (success path, or EXPIRED-with-grace
      // 410 path that carries identification). Otherwise fall back to the
      // cookie-based /api/chat-identity endpoint: if the visitor still holds a
      // valid __Host-vss_confirm_session cookie from a prior visit, the server mints
      // a fresh chat JWT bound to that cookie's contact. If both paths return
      // nothing, the widget loads anonymously.
      let identification:
        | { identificationEmail: string; identificationToken: string }
        | undefined;

      if (result.identificationEmail && result.identificationToken) {
        identification = {
          identificationEmail: result.identificationEmail,
          identificationToken: result.identificationToken,
        };
      } else {
        const cookieId = await fetchChatIdentity();
        if (cancelled) return;
        if (cookieId.identified) {
          identification = {
            identificationEmail: cookieId.identificationEmail,
            identificationToken: cookieId.identificationToken,
          };
        }
      }

      loadChatWidget(identification);
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (
    bookingId: string,
    action: ConfirmAction,
    reason?: DidNotAttendReason | null,
    detail?: DidNotAttendDetail | null,
  ): Promise<boolean> => {
    if (!token) return false;
    setState('submitting');
    const result = await submitConfirmation(token, bookingId, action, reason, detail);

    if (result.success) {
      // Update the ref synchronously so the next submit() in a bundle sees
      // this booking as already-submitted, and base allDone on the ref —
      // not the (potentially stale) state closure.
      submittedActionsRef.current = { ...submittedActionsRef.current, [bookingId]: action };
      setSubmittedActions(submittedActionsRef.current);
      const allDone = bookings.every((b) => submittedActionsRef.current[b.id] !== undefined);
      setState(allDone ? 'submitted' : 'ready');
      return true;
    }

    if (result.status === 410) {
      setState(result.code === 'SUPERSEDED' ? 'superseded' : 'expired');
    } else if (result.status === 409) {
      // 409 is either "already confirmed by staff" or "cancelled by staff".
      // The server sends `code` to disambiguate; map CANCELLED → cancelled,
      // anything else (including missing code from an older server) →
      // already_done, which is the safer default (both are terminal).
      setState(result.code === 'CANCELLED' ? 'cancelled' : 'already_done');
    } else setState('error');
    return false;
  };

  return { state, bookings, submittedActions, submit };
}
